import {
  collection,
  doc as firestoreDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  writeBatch,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { db } from './firebase';
import { getProfessional } from './professionals';
import { getService } from './services';
import { 
  generateAssociationId, 
  updateCounter, 
  validateEntityExists,
  logOperation
} from './utils/firebaseUtils';

/**
 * Obtém uma associação específica entre profissional e serviço
 * @param {string} salonId - ID do salão
 * @param {string} professionalId - ID do profissional
 * @param {string} serviceId - ID do serviço
 * @returns {Promise<Object|null>} Dados da associação ou null se não existir
 */
export const getProfessionalServiceAssociation = async (salonId, professionalId, serviceId) => {
  try {
    const associationId = generateAssociationId(professionalId, serviceId);
    const docRef = firestoreDoc(db, `salons/${salonId}/professional_services/${associationId}`);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    logOperation('ERROR', 'getProfessionalServiceAssociation', `${professionalId}_${serviceId}`, {error: error.message});
    throw error;
  }
};

/**
 * Obtém todas as associações profissional-serviço de um salão
 * @param {string} salonId - ID do salão
 * @returns {Promise<Array>} - Lista de associações
 */
export const getAllProfessionalServiceAssociations = async (salonId) => {
  try {
    const associationsRef = collection(db, `salons/${salonId}/professional_services`);
    const snapshot = await getDocs(associationsRef);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    logOperation('ERROR', 'getAllProfessionalServiceAssociations', salonId, {error: error.message});
    throw error;
  }
};

/**
 * Obtém todos os serviços de um profissional
 * @param {string} salonId - ID do salão
 * @param {string} professionalId - ID do profissional
 * @param {boolean} activeOnly - Se true, retorna apenas associações ativas
 * @returns {Promise<Array>} - Lista de associações profissional-serviço
 */
export const getProfessionalServices = async (salonId, professionalId, activeOnly = false) => {
  try {
    const associationsRef = collection(db, `salons/${salonId}/professional_services`);
    
    let q;
    if (activeOnly) {
      q = query(associationsRef, 
        where('professionalId', '==', professionalId),
        where('isActive', '==', true)
      );
    } else {
      q = query(associationsRef, where('professionalId', '==', professionalId));
    }
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    logOperation('ERROR', 'getProfessionalServices', professionalId, {error: error.message});
    throw error;
  }
};

/**
 * Obtém todos os profissionais que oferecem um serviço
 * @param {string} salonId - ID do salão
 * @param {string} serviceId - ID do serviço
 * @param {boolean} activeOnly - Se true, retorna apenas associações ativas
 * @returns {Promise<Array>} - Lista de associações profissional-serviço
 */
export const getServiceProfessionals = async (salonId, serviceId, activeOnly = false) => {
  try {
    const associationsRef = collection(db, `salons/${salonId}/professional_services`);
    
    let q;
    if (activeOnly) {
      q = query(associationsRef, 
        where('serviceId', '==', serviceId),
        where('isActive', '==', true)
      );
    } else {
      q = query(associationsRef, where('serviceId', '==', serviceId));
    }
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    logOperation('ERROR', 'getServiceProfessionals', serviceId, {error: error.message});
    throw error;
  }
};

/**
 * Atualiza ou cria uma associação entre profissional e serviço
 * @param {string} salonId - ID do salão
 * @param {string} professionalId - ID do profissional
 * @param {string} serviceId - ID do serviço
 * @param {object} metadata - Metadados opcionais (preço e duração personalizados)
 * @returns {Promise<object>} - Dados atualizados da associação
 */
export const updateProfessionalServiceAssociation = async (
  salonId, 
  professionalId, 
  serviceId, 
  associationData = {}
) => {
  try {
    // Validar existência do profissional e serviço
    const profExists = await validateEntityExists(`salons/${salonId}/professionals`, professionalId);
    const serviceExists = await validateEntityExists(`salons/${salonId}/services`, serviceId);
    
    if (!profExists || !serviceExists) {
      const error = new Error(!profExists ? 'Profissional não encontrado' : 'Serviço não encontrado');
      logOperation('ERROR', 'updateProfessionalServiceAssociation', `${professionalId}_${serviceId}`, {error: error.message});
      throw error;
    }
    
    // Buscar dados atuais para denormalização
    const profRef = firestoreDoc(db, `salons/${salonId}/professionals/${professionalId}`);
    const serviceRef = firestoreDoc(db, `salons/${salonId}/services/${serviceId}`);
    
    const [profSnap, serviceSnap] = await Promise.all([
      getDoc(profRef),
      getDoc(serviceRef)
    ]);
    
    const profData = profSnap.data();
    const serviceData = serviceSnap.data();
    
    // Construir documento de associação
    const associationId = generateAssociationId(professionalId, serviceId);
    const docRef = firestoreDoc(db, `salons/${salonId}/professional_services/${associationId}`);
    
    const completeAssociationData = {
      ...associationData,
      professionalId,
      serviceId,
      professionalName: profData.nome || profData.name,
      serviceName: serviceData.nome,
      updatedAt: serverTimestamp()
    };
    
    // Verificar se a associação já existe
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      // Atualizar associação existente
      await updateDoc(docRef, completeAssociationData);
    } else {
      // Criar nova associação
      completeAssociationData.createdAt = serverTimestamp();
      await setDoc(docRef, completeAssociationData);
      
      // Atualizar contadores usando as funções utilitárias
      await updateCounter(`salons/${salonId}/professionals`, professionalId, 'serviceCount', 1);
      await updateCounter(`salons/${salonId}/services`, serviceId, 'professionalCount', 1, 'atualizado_em');
      
      logOperation('SUCCESS', 'updateProfessionalServiceAssociation', associationId, 
        {message: 'Nova associação criada com sucesso'});
    }
    
    return { id: associationId, ...completeAssociationData, updatedAt: new Date() };
  } catch (error) {
    logOperation('ERROR', 'updateProfessionalServiceAssociation', `${professionalId}_${serviceId}`, {error: error.message});
    throw error;
  }
};

/**
 * Remove uma associação específica entre profissional e serviço
 * @param {string} salonId 
 * @param {string} professionalId 
 * @param {string} serviceId 
 * @returns {Promise<boolean>}
 */
export const deleteProfessionalServiceAssociation = async (salonId, professionalId, serviceId) => {
  try {
    const associationId = generateAssociationId(professionalId, serviceId);
    const docRef = firestoreDoc(db, `salons/${salonId}/professional_services/${associationId}`);
    
    // Verificar se a associação existe
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      logOperation('WARN', 'deleteProfessionalServiceAssociation', associationId, 
        {message: 'Associação não encontrada'});
      return false;
    }
    
    // Excluir a associação
    await deleteDoc(docRef);
    
    // Decrementar contadores usando as funções utilitárias
    await updateCounter(`salons/${salonId}/professionals`, professionalId, 'serviceCount', -1);
    await updateCounter(`salons/${salonId}/services`, serviceId, 'professionalCount', -1, 'atualizado_em');
    
    logOperation('SUCCESS', 'deleteProfessionalServiceAssociation', associationId, 
      {message: 'Associação excluída com sucesso'});
    
    return true;
  } catch (error) {
    logOperation('ERROR', 'deleteProfessionalServiceAssociation', `${professionalId}_${serviceId}`, 
      {error: error.message});
    throw error;
  }
};

/**
 * Atualiza várias associações em lote para um profissional
 * @param {string} salonId 
 * @param {string} professionalId 
 * @param {Array} serviceAssociations - Array de objetos {serviceId, customPrice, customDuration, draftPrice, draftDuration}
 * @returns {Promise<Array>}
 */
export const batchUpdateProfessionalServiceAssociations = async (
  salonId, 
  professionalId, 
  serviceAssociations = []
) => {
  try {
    // Validar existência do profissional
    const profRef = firestoreDoc(db, `salons/${salonId}/professionals/${professionalId}`);
    const profSnap = await getDoc(profRef);
    
    if (!profSnap.exists()) {
      const error = new Error('Profissional não encontrado');
      logOperation('ERROR', 'batchUpdateProfessionalServiceAssociations', professionalId, {error: error.message});
      throw error;
    }
    
    const professional = profSnap.data();
    
    // 1. Obter associações atuais
    const currentAssociations = await getProfessionalServices(salonId, professionalId);
    const currentServiceIds = currentAssociations.map(assoc => assoc.serviceId);
    
    // 2. Identificar novos IDs de serviço
    const newServiceIds = serviceAssociations.map(sa => sa.serviceId);
    
    // 3. Identificar serviços a adicionar e remover
    const servicesToAdd = serviceAssociations.filter(sa => !currentServiceIds.includes(sa.serviceId));
    const servicesToRemove = currentAssociations.filter(assoc => !newServiceIds.includes(assoc.serviceId));
    const servicesToKeep = serviceAssociations.filter(sa => currentServiceIds.includes(sa.serviceId));
    
    logOperation('INFO', 'batchUpdateProfessionalServiceAssociations', professionalId, {
      servicesToAdd: servicesToAdd.length,
      servicesToRemove: servicesToRemove.length,
      servicesToKeep: servicesToKeep.length
    });
    
    // Processar em lotes
    const batch = writeBatch(db);
    const servicesRef = {};
    
    // 4. Adicionar novas associações
    const addPromises = servicesToAdd.map(async (sa) => {
      const serviceId = sa.serviceId;
      
      // Validar existência do serviço
      const serviceExists = await validateEntityExists(`salons/${salonId}/services`, serviceId);
      if (!serviceExists) {
        logOperation('WARN', 'batchUpdateProfessionalServiceAssociations', serviceId, 
          {message: 'Serviço não encontrado'});
        return null;
      }
      
      // Buscar dados do serviço para denormalização
      const serviceRef = firestoreDoc(db, `salons/${salonId}/services/${serviceId}`);
      const serviceSnap = await getDoc(serviceRef);
      const serviceData = serviceSnap.data();
      
      // Criar associação
      const associationId = generateAssociationId(professionalId, serviceId);
      const associationRef = firestoreDoc(db, `salons/${salonId}/professional_services/${associationId}`);
      
      batch.set(associationRef, {
        professionalId,
        serviceId,
        professionalName: professional.nome || professional.name,
        serviceName: serviceData.nome,
        customPrice: sa.customPrice === undefined ? null : sa.customPrice,
        customDuration: sa.customDuration === undefined ? null : sa.customDuration,
        draftPrice: sa.draftPrice === undefined ? null : sa.draftPrice,
        draftDuration: sa.draftDuration === undefined ? null : sa.draftDuration,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Armazenar referência para atualizar contador de profissionais
      servicesRef[serviceId] = serviceRef;
      
      return { 
        serviceId, 
        professionalId, 
        serviceName: serviceData.nome,
        professionalName: professional.nome || professional.name
      };
    });
    
    const addedAssociations = (await Promise.all(addPromises)).filter(a => a !== null);
    
    // 5. Remover associações antigas
    servicesToRemove.forEach(assoc => {
      const associationId = generateAssociationId(professionalId, assoc.serviceId);
      const associationRef = firestoreDoc(db, `salons/${salonId}/professional_services/${associationId}`);
      
      batch.delete(associationRef);
      
      // Armazenar referência para atualizar contador de profissionais
      if (!servicesRef[assoc.serviceId]) {
        servicesRef[assoc.serviceId] = firestoreDoc(db, `salons/${salonId}/services/${assoc.serviceId}`);
      }
    });
    
    // 6. Atualizar associações mantidas
    servicesToKeep.forEach(sa => {
      const serviceId = sa.serviceId;
      const associationId = generateAssociationId(professionalId, serviceId);
      const associationRef = firestoreDoc(db, `salons/${salonId}/professional_services/${associationId}`);
      
      batch.update(associationRef, {
        customPrice: sa.customPrice === undefined ? null : sa.customPrice,
        customDuration: sa.customDuration === undefined ? null : sa.customDuration,
        draftPrice: sa.draftPrice === undefined ? null : sa.draftPrice,
        draftDuration: sa.draftDuration === undefined ? null : sa.draftDuration,
        updatedAt: serverTimestamp()
      });
    });
    
    // 7. Atualizar contadores dos serviços
    for (const serviceId in servicesRef) {
      const isAdd = servicesToAdd.some(sa => sa.serviceId === serviceId);
      const isRemove = servicesToRemove.some(sa => sa.serviceId === serviceId);
      
      if (isAdd && !isRemove) {
        batch.update(servicesRef[serviceId], {
          professionalCount: increment(1),
          atualizado_em: serverTimestamp()
        });
      } else if (isRemove && !isAdd) {
        batch.update(servicesRef[serviceId], {
          professionalCount: increment(-1),
          atualizado_em: serverTimestamp()
        });
      }
    }
    
    // Executar em lote
    await batch.commit();
    
    // 8. Atualizar contador de serviços do profissional
    const deltaServiceCount = servicesToAdd.length - servicesToRemove.length;
    
    if (deltaServiceCount !== 0) {
      await updateCounter(`salons/${salonId}/professionals`, professionalId, 'serviceCount', deltaServiceCount);
      
      logOperation('SUCCESS', 'batchUpdateProfessionalServiceAssociations', professionalId, {
        deltaServiceCount,
        novoTotal: (professional.serviceCount || 0) + deltaServiceCount
      });
    }
    
    return {
      added: addedAssociations,
      removed: servicesToRemove.map(a => a.serviceId),
      updated: servicesToKeep.map(a => a.serviceId)
    };
  } catch (error) {
    logOperation('ERROR', 'batchUpdateProfessionalServiceAssociations', professionalId, {error: error.message});
    throw error;
  }
};

/**
 * Gerencia o toggle de personalização de preço e duração para profissional-serviço
 * @param {string} salonId - ID do salão
 * @param {string} professionalId - ID do profissional
 * @param {string} serviceId - ID do serviço
 * @param {boolean} isActive - Estado do switch (ligado/desligado)
 * @param {object} options - Opções adicionais
 * @param {number|null} options.defaultPrice - Preço padrão do serviço (usado quando ativa pela primeira vez)
 * @param {number|null} options.defaultDuration - Duração padrão do serviço (usado quando ativa pela primeira vez)
 * @param {number} options.maxRetries - Número máximo de tentativas em caso de falha (padrão: 2)
 * @param {boolean} options.createIfNotExists - Se deve criar a associação caso não exista (padrão: true)
 * @returns {Promise<Object>} - Dados atualizados da associação
 */
export const toggleProfessionalServiceCustomization = async (
  salonId,
  professionalId,
  serviceId,
  isActive,
  options = {}
) => {
  // Garantir que defaultPrice e defaultDuration sejam números se fornecidos
  const sanitizedOptions = {
    ...options,
    defaultPrice: options.defaultPrice !== undefined ? Number(options.defaultPrice) : null,
    defaultDuration: options.defaultDuration !== undefined ? Number(options.defaultDuration) : null,
    maxRetries: options.maxRetries || 2,
    createIfNotExists: options.createIfNotExists !== false
  };

  const { defaultPrice, defaultDuration, maxRetries, createIfNotExists } = sanitizedOptions;
  let retryCount = 0;
  
  const executeUpdate = async () => {
    try {
      console.log(`[toggleProfessionalServiceCustomization] Profissional=${professionalId}, Serviço=${serviceId}, isActive=${isActive}, tentativa=${retryCount + 1}`);
      console.log(`[toggleProfessionalServiceCustomization] salonId=${salonId}`);
      
      // Gerar ID da associação
      const associationId = generateAssociationId(professionalId, serviceId);
      const associationRef = firestoreDoc(db, `salons/${salonId}/professional_services/${associationId}`);
      
      // Buscar a associação atual diretamente
      const associationSnap = await getDoc(associationRef);
      
      if (!associationSnap.exists()) {
        console.log(`[toggleProfessionalServiceCustomization] Associação não encontrada: ${associationId}`);
        
        if (createIfNotExists) {
          console.log(`[toggleProfessionalServiceCustomization] Criando nova associação`);
          
          try {
            // Buscar dados do profissional e serviço para criar a associação
            const profRef = firestoreDoc(db, `salons/${salonId}/professionals/${professionalId}`);
            const serviceRef = firestoreDoc(db, `salons/${salonId}/services/${serviceId}`);
            
            const [profSnap, serviceSnap] = await Promise.all([
              getDoc(profRef),
              getDoc(serviceRef)
            ]);
            
            if (!profSnap.exists()) {
              console.error(`[toggleProfessionalServiceCustomization] Profissional não encontrado: ${professionalId}`);
              throw new Error(`Profissional não encontrado: ${professionalId}`);
            }
            
            if (!serviceSnap.exists()) {
              console.error(`[toggleProfessionalServiceCustomization] Serviço não encontrado: ${serviceId}`);
              throw new Error(`Serviço não encontrado: ${serviceId}`);
            }
            
            const profData = profSnap.data();
            const serviceData = serviceSnap.data();
            
            // Criar dados da associação
            const newAssociationData = {
              professionalId,
              serviceId,
              professionalName: profData.nome || profData.name,
              serviceName: serviceData.nome,
              customPrice: isActive ? defaultPrice : null,
              customDuration: isActive ? defaultDuration : null,
              draftPrice: null,
              draftDuration: null,
              isActive: true,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            };
            
            console.log(`[toggleProfessionalServiceCustomization] Dados da nova associação:`, newAssociationData);
            
            // Criar a associação no Firebase
            await setDoc(associationRef, newAssociationData);
            
            // Atualizar contadores
            await updateCounter(`salons/${salonId}/professionals`, professionalId, 'serviceCount', 1);
            await updateCounter(`salons/${salonId}/services`, serviceId, 'professionalCount', 1, 'atualizado_em');
            
            console.log(`[toggleProfessionalServiceCustomization] Associação criada com sucesso`);
            
            // Retornar os dados da associação recém-criada
            return {
              id: associationId,
              ...newAssociationData,
              updatedAt: new Date()
            };
          } catch (createError) {
            console.error(`[toggleProfessionalServiceCustomization] Erro ao criar associação:`, createError);
            throw createError;
          }
        } else {
          console.error(`[toggleProfessionalServiceCustomization] Associação não encontrada e createIfNotExists=false`);
          throw new Error('Associação profissional-serviço não encontrada');
        }
      }
      
      const association = associationSnap.data();
      console.log(`[toggleProfessionalServiceCustomization] Associação atual:`, association);
      
      // Estabelecer os valores a serem atualizados com base no estado do switch
      const updateData = {};
      
      if (isActive) {
        // Ativar personalização
        console.log(`[toggleProfessionalServiceCustomization] Ativando personalização`);
        console.log(`[toggleProfessionalServiceCustomization] Valores disponíveis: defaultPrice=${defaultPrice}, defaultDuration=${defaultDuration}`);
        console.log(`[toggleProfessionalServiceCustomization] Valores de rascunho: draftPrice=${association.draftPrice !== undefined ? association.draftPrice : 'undefined'}, draftDuration=${association.draftDuration !== undefined ? association.draftDuration : 'undefined'}`);
        
        // Se existem rascunhos, usar esses valores, se não, usar valores padrão
        updateData.customPrice = association.draftPrice !== undefined && association.draftPrice !== null
          ? association.draftPrice 
          : defaultPrice;
          
        updateData.customDuration = association.draftDuration !== undefined && association.draftDuration !== null
          ? association.draftDuration 
          : defaultDuration;
          
        // Limpar os rascunhos
        updateData.draftPrice = null;
        updateData.draftDuration = null;
      } else {
        // Desativar personalização
        console.log(`[toggleProfessionalServiceCustomization] Desativando personalização`);
        
        // Guardar valores atuais como rascunho
        updateData.draftPrice = association.customPrice !== undefined && association.customPrice !== null
          ? association.customPrice 
          : null;
          
        updateData.draftDuration = association.customDuration !== undefined && association.customDuration !== null
          ? association.customDuration 
          : null;
          
        // IMPORTANTE: Definir explicitamente customPrice e customDuration como null
        updateData.customPrice = null;
        updateData.customDuration = null;
      }
      
      // Adicionar timestamp
      updateData.updatedAt = serverTimestamp();
      
      console.log(`[toggleProfessionalServiceCustomization] Dados a atualizar:`, updateData);
      
      // Forçar a atualização do documento no Firebase
      try {
        await updateDoc(associationRef, updateData);
        console.log(`[toggleProfessionalServiceCustomization] Documento atualizado com sucesso no Firebase`);
        
        // Verificar se a atualização foi bem-sucedida
        const updatedSnap = await getDoc(associationRef);
        const updatedData = updatedSnap.data();
        console.log(`[toggleProfessionalServiceCustomization] Dados após atualização:`, updatedData);
        
        // Verificar explicitamente se os campos foram atualizados corretamente
        if (!isActive && (updatedData.customPrice !== null || updatedData.customDuration !== null)) {
          console.error(`[toggleProfessionalServiceCustomization] ALERTA: Os campos não foram atualizados corretamente!`);
          console.error(`customPrice=${updatedData.customPrice}, customDuration=${updatedData.customDuration}`);
          
          // Se os campos não foram definidos como null, forçar nova tentativa com set em vez de update
          if (retryCount < maxRetries) {
            console.log(`[toggleProfessionalServiceCustomization] Tentando novamente com set em vez de update`);
            
            // Usar setDoc para forçar a atualização de todos os campos
            const completeData = {
              ...updatedData,
              ...updateData,
              customPrice: null,  // Forçar explicitamente null
              customDuration: null  // Forçar explicitamente null
            };
            
            await setDoc(associationRef, completeData);
            
            // Verificar novamente
            const finalSnap = await getDoc(associationRef);
            const finalData = finalSnap.data();
            
            console.log(`[toggleProfessionalServiceCustomization] Dados após retry:`, finalData);
            
            return {
              id: associationId,
              ...finalData,
              updatedAt: new Date()
            };
          }
        }
        
        // Retornar os dados atualizados
        return {
          id: associationId,
          ...updatedData,
          updatedAt: new Date() // Converter o timestamp para Date para uso imediato
        };
      } catch (updateError) {
        console.error(`[toggleProfessionalServiceCustomization] Erro ao atualizar documento:`, updateError);
        throw updateError;
      }
    } catch (error) {
      if (retryCount < maxRetries) {
        console.log(`[toggleProfessionalServiceCustomization] Tentativa ${retryCount + 1} falhou, tentando novamente...`);
        retryCount++;
        return executeUpdate();
      }
      
      console.error(`[toggleProfessionalServiceCustomization] Erro após ${retryCount + 1} tentativas:`, error);
      logOperation('ERROR', 'toggleProfessionalServiceCustomization', `${professionalId}_${serviceId}`, {error: error.message});
      throw error;
    }
  };
  
  return executeUpdate();
};

const sanitizeCustomizations = (customizations) => {
  if (!customizations) return {};
  
  const sanitized = {};
  
  Object.keys(customizations).forEach(profId => {
    if (!customizations[profId]) {
      sanitized[profId] = {
        customPrice: null,
        customDuration: null,
        draftPrice: null,
        draftDuration: null
      };
      return;
    }
    
    // Garantir que todas as propriedades existam com um valor válido para o Firestore
    sanitized[profId] = {
      customPrice: customizations[profId].customPrice === undefined ? null : customizations[profId].customPrice,
      customDuration: customizations[profId].customDuration === undefined ? null : customizations[profId].customDuration,
      draftPrice: customizations[profId].draftPrice === undefined ? null : customizations[profId].draftPrice,
      draftDuration: customizations[profId].draftDuration === undefined ? null : customizations[profId].draftDuration
    };
    
    // Verificação adicional para cada propriedade
    Object.keys(sanitized[profId]).forEach(key => {
      if (sanitized[profId][key] === undefined) {
        sanitized[profId][key] = null;
      }
    });
  });
  
  return sanitized;
};

export const updateService = async (salonId, serviceId, serviceData) => {
  try {
    // Criar referência ao documento do serviço
    const serviceRef = firestoreDoc(db, `salons/${salonId}/services/${serviceId}`);

    // Sanitize o objeto professionalCustomizations para remover valores undefined
    if (serviceData.professionalCustomizations) {
      serviceData.professionalCustomizations = sanitizeCustomizations(serviceData.professionalCustomizations);
    }
    
    // Agora é seguro enviar para o Firestore
    await updateDoc(serviceRef, serviceData);
    
    return { ...serviceData, id: serviceId };
  } catch (error) {
    console.error('[updateService] Erro ao atualizar serviço:', error);
    throw error;
  }
};
